import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Loader2, ShieldCheck, Eye, EyeOff, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function AcceptInvite() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const checkInvite = useCallback(() => {
    if (!token) { setInvalid(true); setChecking(false); return; }
    api.get('/team/invite-info', { params: { token } })
      .then((r) => {
        if (r.data?.valid) setInfo(r.data);
        else setInvalid(true);
      })
      .catch(() => setInvalid(true))
      .finally(() => setChecking(false));
  }, [token]);

  useEffect(() => {
    const t = setTimeout(checkInvite, 0);
    return () => clearTimeout(t);
  }, [checkInvite]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/team/accept', { token, name: name.trim(), password });
      setSession(res.data);
      toast.success(`Welcome to ${info?.workspace_name || 'the team'}!`);
      navigate('/app/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept the invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="accept-invite-page">
      <div className="hidden lg:flex relative tg-noise bg-primary flex-col justify-between p-12 text-primary-foreground">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-primary-foreground/15 flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-display text-xl font-semibold">Thrive Gatherings</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-semibold leading-tight">You&apos;re invited to<br/>join the team.</h2>
          <p className="mt-4 text-primary-foreground/80 max-w-md">Accept your invitation to help create and manage events, contacts and more — together.</p>
        </div>
        <span className="text-sm text-primary-foreground/60">thrivegathering.life</span>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          {checking ? (
            <div className="flex items-center justify-center py-24" data-testid="invite-checking">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invalid ? (
            <div className="text-center" data-testid="invite-invalid">
              <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-7 w-7 text-muted-foreground" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Invitation not valid</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This invitation link is invalid, has expired, or has already been used. Please ask for a fresh invite.
              </p>
              <Button asChild variant="secondary" className="mt-6"><Link to="/login">Go to sign in</Link></Button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium mb-4">
                <UserPlus className="h-3.5 w-3.5" /> Admin invitation
              </div>
              <h1 className="font-display text-3xl font-semibold" data-testid="invite-workspace-name">Join {info.workspace_name}</h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {info.invited_by_name ? `${info.invited_by_name} invited ` : 'You were invited '}
                <span className="text-foreground font-medium">{info.email}</span> to become an admin. Set a password to finish.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="invite-name-input" placeholder="Your full name" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pw">Create password</Label>
                  <div className="relative">
                    <Input id="pw" type={showPw ? 'text' : 'password'} required value={password}
                      onChange={(e) => setPassword(e.target.value)} data-testid="invite-password-input"
                      placeholder="At least 8 characters" className="h-11 pr-10" />
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPw ? 'Hide password' : 'Show password'}>
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type={showPw ? 'text' : 'password'} required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm-input"
                    placeholder="Re-enter password" className="h-11" />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-destructive">Passwords do not match.</p>
                  )}
                </div>
                <Button type="submit" disabled={submitting} data-testid="invite-accept-button" className="w-full h-11 gap-2">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  {submitting ? 'Setting up…' : 'Accept & join'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
